// ホスト用サイドパネル (M4)。8 ボタンの集計表示と一括リセット。
// M5 でワンボタン投下、M7 でチャット監視 + AI 候補、M8 でセクションマーカー / タイマー、
// M6 で用語アシストを順次追加していく。

import { useEffect, useState, type ReactElement } from 'react';
import { DEFAULT_LOCALE, t } from '@solo-webinar-copilot/i18n';
import {
  fetchState,
  postReset,
  type SessionState,
} from '../lib/api-client.ts';
import { BUTTONS, PROGRESS_BUTTON_TYPES } from '../lib/buttons.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';
import { QuickMessages } from './QuickMessages.tsx';
import { Terms } from './Terms.tsx';
import { ChatMonitor } from './ChatMonitor.tsx';
import { Faqs } from './Faqs.tsx';
import { SearchLog } from './SearchLog.tsx';
import { TimeKeeper } from './TimeKeeper.tsx';

const POLL_INTERVAL_MS = 3000;

export interface HostPanelProps {
  ctx: AppContext;
}

export function HostPanel({ ctx }: HostPanelProps): ReactElement {
  const locale = DEFAULT_LOCALE;
  const [state, setState] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const s = await fetchState(ctx);
        if (!cancelled) {
          setState(s);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [ctx]);

  const handleReset = async () => {
    setResetMessage(null);
    const r = await postReset(ctx, PROGRESS_BUTTON_TYPES);
    if (r.ok) {
      setResetMessage(t('in_client.host.reset_done', locale));
      // 即時に集計を更新
      try {
        const s = await fetchState(ctx);
        setState(s);
      } catch {
        // 次の poll で更新されるので無視
      }
    } else {
      setError(r.error);
    }
  };

  return (
    <div data-testid="host-panel">
      <header>
        <h1>{t('in_client.host.title', locale)}</h1>
        {ctx.isMock ? <small data-testid="mock-badge">[MOCK]</small> : null}
      </header>
      <section>
        <p>
          {t('in_client.host.participants', locale)}: {state?.participantCount ?? '—'}
        </p>
      </section>
      <section>
        {error ? (
          <p role="alert" data-testid="host-error">
            {t('in_client.error.network', locale)}: {error}
          </p>
        ) : null}
        <table data-testid="counts-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {BUTTONS.map((b) => {
              const n = state?.counts[b.type] ?? 0;
              return (
                <tr key={b.type} data-testid={`count-row-${b.type}`}>
                  <td style={{ padding: '4px 8px' }}>
                    <span aria-hidden="true" style={{ marginRight: 6 }}>
                      {b.icon}
                    </span>
                    {t(b.labelKey, locale)}
                  </td>
                  <td
                    style={{ padding: '4px 8px', textAlign: 'right' }}
                    data-testid={`count-${b.type}`}
                  >
                    {n}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section>
        <button
          type="button"
          onClick={() => void handleReset()}
          data-testid="reset-button"
        >
          {t('in_client.host.reset_progress', locale)}
        </button>
        {resetMessage ? (
          <p data-testid="reset-message">{resetMessage}</p>
        ) : null}
      </section>
      <TimeKeeper ctx={ctx} locale={locale} />
      <QuickMessages ctx={ctx} locale={locale} />
      <Terms ctx={ctx} locale={locale} />
      <ChatMonitor ctx={ctx} locale={locale} />
      <Faqs ctx={ctx} locale={locale} />
      <SearchLog ctx={ctx} locale={locale} />
    </div>
  );
}
